import { Button, Icon } from "@app/ui";
import { useT, useFormatDateTime } from "../i18n.js";
import type { PublicUser, SecurityOverview } from "../types.js";

/**
 * The account's open security work, as a scored list. It is the page's lead
 * because it is the only part of a profile that is actionable rather than
 * informational — everything else here just states a fact.
 *
 * Scoring is deliberately blunt: each item is worth the same, and an item only
 * counts as done when the API says so (verified flag, mfaEnabled, password age
 * vs. the installation's rotation policy). Nothing is inferred client-side.
 */

export interface SecurityCheck {
  key: string;
  title: string;
  description: string;
  done: boolean;
  /** Omitted for a done item — there is nothing left to do. */
  action?: { label: string; onClick: () => void };
}

/** Password rotation nudge when the installation has no policy of its own. */
const DEFAULT_MAX_AGE_DAYS = 365;

const daysSince = (iso: string): number => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

export function useSecurityChecks(
  user: PublicUser | null,
  overview: SecurityOverview | null,
  actions: { verifyEmail: () => void; enableMfa: () => void; changePassword: () => void },
): SecurityCheck[] {
  const t = useT();
  const fmtDate = useFormatDateTime();

  if (!user) return [];

  const checks: SecurityCheck[] = [
    {
      key: "email",
      title: t("profile.checkEmail"),
      description: user.emailVerified ? t("profile.checkEmailDone") : t("profile.checkEmailTodo"),
      done: user.emailVerified,
      ...(user.emailVerified
        ? {}
        : { action: { label: t("profile.verifyNow"), onClick: actions.verifyEmail } }),
    },
    {
      key: "mfa",
      title: t("profile.checkMfa"),
      description: user.mfaEnabled ? t("profile.checkMfaDone") : t("profile.checkMfaTodo"),
      done: user.mfaEnabled,
      ...(user.mfaEnabled ? {} : { action: { label: t("profile.checkAction"), onClick: actions.enableMfa } }),
    },
  ];

  // The password item needs the server's timestamp, so it only joins the list
  // once the overview has loaded — a half-scored list would be misleading.
  if (overview) {
    const age = daysSince(overview.passwordChangedAt);
    const limit = overview.passwordMaxAgeDays ?? DEFAULT_MAX_AGE_DAYS;
    const fresh = age < limit;
    checks.push({
      key: "password",
      title: t("profile.checkPassword"),
      description: fresh
        ? t("profile.checkPasswordDone", { date: fmtDate(overview.passwordChangedAt) })
        : t("profile.checkPasswordTodo", { days: age }),
      done: fresh,
      ...(fresh ? {} : { action: { label: t("profile.checkAction"), onClick: actions.changePassword } }),
    });
  }

  return checks;
}

export function SecurityChecklist({ checks }: { checks: SecurityCheck[] }) {
  return (
    <div className="pf-check">
      {checks.map((c) => (
        <div key={c.key} className={`pf-check__row${c.done ? " is-done" : ""}`}>
          <span className="pf-check__mark">
            <Icon name={c.done ? "check" : "warning"} size={15} />
          </span>
          <div className="pf-check__body">
            <span className="pf-check__title">{c.title}</span>
            <span className="pf-check__desc">{c.description}</span>
          </div>
          {c.action && (
            <Button size="sm" variant="primary" onClick={c.action.onClick}>
              {c.action.label}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
